import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';

const ICON_BASE_URL = 'https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/icons2';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  subject?: string;
  message?: string;
}

function Contact() {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('contact.form.errors.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contact.form.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.form.errors.emailInvalid');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('contact.form.errors.phoneRequired');
    }

    if (!formData.subject.trim()) {
      newErrors.subject = t('contact.form.errors.subjectRequired');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contact.form.errors.messageRequired');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t('contact.form.errors.messageTooShort');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Submit to Supabase
      const { error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: formData.name.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            subject: formData.subject,
            message: formData.message.trim(),
            user_agent: navigator.userAgent
          }
        ]);

      if (error) {
        throw error;
      }

      // Reset form on success
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setSubmitSuccess(true);

      // Hide success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError(
        error instanceof Error 
          ? error.message 
          : 'Failed to submit form. Please try again or contact us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: `${ICON_BASE_URL}/phone-income-front-color.png`,
      title: t('contact.info.phone'),
      value: '+1 (361) 510-6451',
      link: 'tel:+13615106451'
    },
    {
      icon: `${ICON_BASE_URL}/mail-front-color.png`,
      title: t('contact.info.email'),
      value: 'nhenaoga@gmail.com',
      link: 'mailto:nhenaoga@gmail.com'
    },
    {
      icon: `${ICON_BASE_URL}/location-front-color.png`,
      title: t('contact.info.location'),
      value: 'San Antonio, TX',
      link: null
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-baby-blue-200 via-baby-blue-50 to-pink-200 px-4 py-16 md:py-20 lg:py-28 min-h-[300px] md:min-h-[350px] overflow-hidden">
        {/* Grid Pattern Background */}
        <div
          className="absolute inset-0 bg-grid-light"
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-4 md:mb-6">
            {t('contact.hero.title')}
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-gray-700 max-w-3xl mx-auto">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="px-4 py-8 md:px-8 md:py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-white border-2 border-blue-100 rounded-xl p-6 md:p-8 text-center hover:border-pink-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex justify-center mb-4">
                  <div className="bg-blue-50 rounded-full p-4">
                    <img
                      src={info.icon}
                      alt={info.title}
                      className="w-12 h-12 md:w-16 md:h-16"
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  {info.title}
                </h3>
                {info.link ? (
                  <a
                    href={info.link}
                    className="text-base md:text-lg text-blue-600 hover:text-pink-600 transition-colors duration-300"
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-base md:text-lg text-gray-700">{info.value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="px-4 py-8 md:px-8 md:py-12 lg:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('contact.form.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-700">
              {t('contact.form.subtitle')}
            </p>
          </div>

          {/* Success Message */}
          {submitSuccess && (
            <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-xl p-4 md:p-6">
              <div className="flex items-start gap-3">
                <img
                  src={`${ICON_BASE_URL}/bell-front-color.png`}
                  alt="Success"
                  className="w-6 h-6 mt-0.5"
                  crossOrigin="anonymous"
                />
                <div>
                  <h3 className="font-bold text-green-900 mb-1">
                    {t('contact.form.successTitle')}
                  </h3>
                  <p className="text-green-800 text-sm md:text-base">
                    {t('contact.form.successMessage')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {submitError && (
            <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4 md:p-6">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-white text-sm font-bold">!</span>
                </div>
                <div>
                  <h3 className="font-bold text-red-900 mb-1">
                    Submission Failed
                  </h3>
                  <p className="text-red-800 text-sm md:text-base">
                    {submitError}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 md:p-8 lg:p-10">
            {/* Name Field */}
            <div className="mb-6">
              <label htmlFor="name" className="block text-gray-900 font-semibold mb-2">
                {t('contact.form.name')} <span className="text-pink-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300 ${
                  errors.name ? 'border-red-400' : 'border-gray-300'
                }`}
                placeholder={t('contact.form.namePlaceholder')}
              />
              {errors.name && (
                <p className="mt-2 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Email and Phone Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-gray-900 font-semibold mb-2">
                  {t('contact.form.email')} <span className="text-pink-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300 ${
                    errors.email ? 'border-red-400' : 'border-gray-300'
                  }`}
                  placeholder={t('contact.form.emailPlaceholder')}
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="phone" className="block text-gray-900 font-semibold mb-2">
                  {t('contact.form.phone')} <span className="text-pink-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300 ${
                    errors.phone ? 'border-red-400' : 'border-gray-300'
                  }`}
                  placeholder={t('contact.form.phonePlaceholder')}
                />
                {errors.phone && (
                  <p className="mt-2 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>
            </div>

            {/* Subject Field */}
            <div className="mb-6">
              <label htmlFor="subject" className="block text-gray-900 font-semibold mb-2">
                {t('contact.form.subject')} <span className="text-pink-500">*</span>
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className={`w-full h-12 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300 ${
                  errors.subject ? 'border-red-400' : 'border-gray-300'
                }`}
              >
                <option value="">{t('contact.form.subjectPlaceholder')}</option>
                <option value="general">{t('contact.form.subjects.general')}</option>
                <option value="website">{t('contact.form.subjects.website')}</option>
                <option value="support">{t('contact.form.subjects.support')}</option>
                <option value="partnership">{t('contact.form.subjects.partnership')}</option>
                <option value="other">{t('contact.form.subjects.other')}</option>
              </select>
              {errors.subject && (
                <p className="mt-2 text-sm text-red-600">{errors.subject}</p>
              )}
            </div>

            {/* Message Field */}
            <div className="mb-6">
              <label htmlFor="message" className="block text-gray-900 font-semibold mb-2">
                {t('contact.form.message')} <span className="text-pink-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300 resize-none ${
                  errors.message ? 'border-red-400' : 'border-gray-300'
                }`}
                placeholder={t('contact.form.messagePlaceholder')}
              />
              {errors.message && (
                <p className="mt-2 text-sm text-red-600">{errors.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto h-12 px-8 bg-gradient-to-r from-blue-400 to-blue-500 text-white font-bold rounded-lg hover:from-blue-500 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: ''
                  });
                  setErrors({});
                }}
                className="w-full sm:w-auto h-12 px-8 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 transition-all duration-300"
              >
                {t('contact.form.reset')}
              </button>
            </div>
          </form>

          {/* Additional Info */}
          <div className="mt-8 text-center">
            <p className="text-sm md:text-base text-gray-600">
              {t('contact.form.privacy')}
            </p>
          </div>
        </div>
      </section>

      {/* Business Hours Section */}
      <section className="px-4 py-8 md:px-8 md:py-12 lg:py-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-50 to-pink-50 rounded-xl p-6 md:p-8 lg:p-10">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <img
                  src={`${ICON_BASE_URL}/clock-front-color.png`}
                  alt="Business Hours"
                  className="w-16 h-16 md:w-20 md:h-20"
                  crossOrigin="anonymous"
                />
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                {t('contact.hours.title')}
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-6">
                {t('contact.hours.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="font-bold text-gray-900 mb-1">{t('contact.hours.weekdays')}</p>
                <p className="text-gray-700">9:00 AM - 6:00 PM</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="font-bold text-gray-900 mb-1">{t('contact.hours.weekends')}</p>
                <p className="text-gray-700">10:00 AM - 4:00 PM</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
